/**
 * useTheme — shared theme hook for all core-loop.dev apps
 *
 * Persists the user's dark/light preference in a cookie scoped to
 * .core-loop.dev so the choice is shared across all subdomains
 * (core-loop.dev, finance.core-loop.dev, blog.core-loop.dev, etc.)
 *
 * Falls back to localStorage for local dev (no shared domain).
 *
 * Usage:
 *   const { theme, toggleTheme } = useTheme();
 */

import { useState, useEffect, useCallback } from 'react';

const COOKIE_KEY = 'core-loop.theme';
const STORAGE_KEY = 'core-loop.theme';
const DEFAULT_THEME = 'dark';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year in seconds

function isSubdomain(): boolean {
  try {
    return window.location.hostname.endsWith('.core-loop.dev') ||
           window.location.hostname === 'core-loop.dev';
  } catch { return false; }
}

function readCookie(name: string): string | null {
  try {
    // eslint-disable-next-line @microsoft/sdl/no-cookies -- theme preference only; no PII, no auth; SameSite=Lax; cross-subdomain sharing
    const match = document.cookie.split(';')
      .map(c => c.trim())
      .find(c => c.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
  } catch { return null; }
}

function writeCookie(name: string, value: string): void {
  try {
    const domain = isSubdomain() ? '; domain=.core-loop.dev' : '';
    // eslint-disable-next-line @microsoft/sdl/no-cookies -- theme preference only; no PII, no auth; SameSite=Lax; cross-subdomain sharing
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax${domain}`;
    // Keep localStorage in sync for the inline init script (flicker prevention)
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch { /* ignore */ }
}

function readInitialTheme() {
  // 1. Cookie (cross-subdomain, authoritative)
  const cookie = readCookie(COOKIE_KEY);
  if (cookie === 'light' || cookie === 'dark') return cookie;
  // 2. localStorage fallback (local dev / first visit before cookie set)
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* ignore */ }
  // 3. OS preference
  try {
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  } catch { /* ignore */ }
  return DEFAULT_THEME;
}

function applyTheme(theme: string) {
  document.documentElement.setAttribute('data-theme', theme === 'light' ? 'light' : 'dark');
}

export function useTheme() {
  const [theme, setTheme] = useState(() => {
    const initial = readInitialTheme();
    applyTheme(initial);
    return initial;
  });

  useEffect(() => {
    applyTheme(theme);
    writeCookie(COOKIE_KEY, theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(t => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  return { theme, toggleTheme };
}
